import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

type Status =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '1x00000000000000000000AA';

export function Contact() {
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const submitting = status.kind === 'submitting';
  const canSubmit =
    !submitting &&
    token !== null &&
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    message.trim().length >= 10;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus({ kind: 'submitting' });
    try {
      const r = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message, turnstileToken: token }),
      });
      const data = (await r.json().catch(() => ({}))) as { error?: string };
      if (!r.ok) {
        const msg = data.error ?? `Request failed (${r.status})`;
        setStatus({ kind: 'error', message: msg });
        turnstileRef.current?.reset();
        setToken(null);
        return;
      }
      setStatus({ kind: 'success' });
      setName('');
      setEmail('');
      setMessage('');
      turnstileRef.current?.reset();
      setToken(null);
    } catch {
      setStatus({
        kind: 'error',
        message: 'Network error — please try again in a moment.',
      });
      turnstileRef.current?.reset();
      setToken(null);
    }
  }

  return (
    <Box
      component="section"
      id="contact"
      sx={{ backgroundColor: 'background.default', py: { xs: 10, md: 14 } }}
    >
      <Container maxWidth="sm">
        <Stack spacing={2} sx={{ textAlign: 'center', alignItems: 'center', mb: { xs: 5, md: 7 } }}>
          <Typography variant="overline" sx={{ color: 'primary.main' }}>
            Free connection call
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{ color: 'primary.dark', fontSize: { xs: '2rem', md: '2.7rem' } }}
          >
            Your growth starts here.
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 520 }}>
            Tell Logan a little about you and what you&rsquo;re working on. Most replies go out
            within one business day &mdash; or call <Box component="a" href="tel:+13176958915" sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none', borderBottom: '1px solid currentColor' }}>317-695-8915</Box>.
          </Typography>
        </Stack>

        {status.kind === 'success' ? (
          <Alert
            severity="success"
            sx={{ borderRadius: 1, fontSize: '1rem' }}
            onClose={() => setStatus({ kind: 'idle' })}
          >
            Thanks — your message is on its way. Logan will be in touch shortly.
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2.5}>
              <TextField
                label="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <TextField
                label="What&rsquo;s on your mind?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                multiline
                rows={5}
                helperText={
                  message.length > 0 && message.trim().length < 10
                    ? 'A few more words helps Logan respond well.'
                    : ' '
                }
              />

              <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 65 }}>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={SITE_KEY}
                  options={{ theme: 'light', size: 'flexible' }}
                  onSuccess={setToken}
                  onError={() => setToken(null)}
                  onExpire={() => setToken(null)}
                />
              </Box>

              {status.kind === 'error' ? (
                <Alert severity="error" sx={{ borderRadius: 1 }}>
                  {status.message}
                </Alert>
              ) : null}

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={!canSubmit}
                startIcon={
                  submitting ? <CircularProgress size={18} color="inherit" /> : <SendRoundedIcon />
                }
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' }, minWidth: 200 }}
              >
                {submitting ? 'Sending…' : 'Grow with a coach'}
              </Button>
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
}
